/*
 * THE SHIPS. Asked for on 2026-10-06: "Add a boat from the Grey Havens to
 * somewhere near Minas Tirith per the lore".
 *
 * One ship moored at the end of a jetty at each end of the sea road: Círdan's
 * at the Grey Havens and the one at the Harlond below Minas Tirith (see
 * ships.go). The passage is asked of the NPC on the jetty; the ship is what
 * shows there is one to be had.
 *
 * It is a grey ship of the Havens: a long pale hull with a swan's neck for a
 * prow, as the ships of the Teleri were made, a single mast with its white
 * sail half furled on the yard while it lies at the quay, and a pennant.
 *
 * Two pictures, as the Party Tree has (see partytree.ts). Flat (shipFlat), a
 * tile seen from above for the flat views: the water, and the hull as a long
 * pale shape with the mast and the furled sail across it. Standing
 * (shipPicture), stood up on its tile in the oblique view, side on, three
 * tiles long, its waterline across the middle of its tile.
 *
 * It is kept in a file of its own and imports nothing, so neither the
 * sprites nor the renderer have to grow round it.
 */

/** The standing picture's size, in world pixels: three tiles long and over three high. */
export const SHIP_W = 100;
export const SHIP_H = 108;
/** Where the waterline's middle is in the standing picture, from its top-left. */
export const SHIP_FOOT = { x: SHIP_W / 2, y: SHIP_H - 14 };

const HULL = { light: "#e4e0d6", mid: "#bdb8ad", dark: "#8c877e", keel: "#5e5a54" };
const WOOD = { mast: "#6a5034", dark: "#4a3824" };
const SAIL = { light: "#f6f3ea", shade: "#d8d3c6" };
const WATER = { line: "#9cc2ee", deep: "rgba(20, 40, 80, 0.35)" };

type Ctx = CanvasRenderingContext2D;

let standing: HTMLCanvasElement | null = null;

/** The ship stood up side on, made once. */
export function shipPicture(): HTMLCanvasElement {
  if (standing) return standing;
  const cv = document.createElement("canvas");
  cv.width = SHIP_W;
  cv.height = SHIP_H;
  // Kept off the graphics card, as keptContext in paper.ts says why.
  const ctx = cv.getContext("2d", { willReadFrequently: true })!;
  const wl = SHIP_FOOT.y; // the waterline
  const stern = 8, bow = SHIP_W - 14;

  // Its shadow on the water.
  ctx.fillStyle = WATER.deep;
  ctx.beginPath();
  ctx.ellipse(SHIP_W / 2, wl + 4, 44, 6, 0, 0, Math.PI * 2);
  ctx.fill();

  // The hull: a long shallow curve, higher at the stern and at the bow.
  const hull = (top: number, fill: string) => {
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.moveTo(stern, top - 8);
    ctx.quadraticCurveTo(SHIP_W / 2, top + 2, bow, top - 10);
    ctx.lineTo(bow - 6, wl + 2);
    ctx.quadraticCurveTo(SHIP_W / 2, wl + 8, stern + 6, wl + 1);
    ctx.closePath();
    ctx.fill();
  };
  hull(wl - 16, HULL.mid);
  hull(wl - 18, HULL.light);
  // The lower strakes, darker towards the water.
  ctx.fillStyle = HULL.dark;
  ctx.beginPath();
  ctx.moveTo(stern + 5, wl - 4);
  ctx.quadraticCurveTo(SHIP_W / 2, wl + 1, bow - 5, wl - 6);
  ctx.lineTo(bow - 6, wl + 2);
  ctx.quadraticCurveTo(SHIP_W / 2, wl + 8, stern + 6, wl + 1);
  ctx.closePath();
  ctx.fill();
  // A line along the gunwale.
  ctx.strokeStyle = HULL.keel;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(stern + 1, wl - 25);
  ctx.quadraticCurveTo(SHIP_W / 2, wl - 15, bow - 1, wl - 27);
  ctx.stroke();

  // The swan's neck at the bow, curving up and over, with its head.
  ctx.strokeStyle = HULL.light;
  ctx.lineWidth = 4;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(bow - 2, wl - 26);
  ctx.quadraticCurveTo(bow + 10, wl - 34, bow + 6, wl - 48);
  ctx.quadraticCurveTo(bow + 2, wl - 56, bow + 9, wl - 56);
  ctx.stroke();
  ctx.fillStyle = "#e0b040"; // the beak
  ctx.fillRect(bow + 9, wl - 57, 4, 2);
  ctx.fillStyle = HULL.keel; // the eye
  ctx.fillRect(bow + 6, wl - 57, 1, 1);
  // The stern post, curling over as a tail.
  ctx.beginPath();
  ctx.moveTo(stern + 2, wl - 24);
  ctx.quadraticCurveTo(stern - 4, wl - 32, stern + 2, wl - 38);
  ctx.stroke();
  ctx.lineCap = "butt";

  // The mast and the yard.
  const mx = SHIP_W / 2 - 2;
  ctx.fillStyle = WOOD.mast;
  ctx.fillRect(mx, 8, 3, wl - 26);
  ctx.fillStyle = WOOD.dark;
  ctx.fillRect(mx - 26, 20, 55, 3);
  // The sail, half furled: a bellied band under the yard, gathered in folds.
  ctx.fillStyle = SAIL.light;
  ctx.beginPath();
  ctx.moveTo(mx - 25, 23);
  ctx.lineTo(mx + 28, 23);
  ctx.quadraticCurveTo(mx + 26, 40, mx + 18, 46);
  ctx.quadraticCurveTo(mx + 1, 52, mx - 16, 46);
  ctx.quadraticCurveTo(mx - 24, 40, mx - 25, 23);
  ctx.fill();
  ctx.strokeStyle = SAIL.shade;
  ctx.lineWidth = 1;
  for (const dx of [-14, -4, 6, 16]) {
    ctx.beginPath();
    ctx.moveTo(mx + dx, 24);
    ctx.quadraticCurveTo(mx + dx + 1, 36, mx + dx * 0.8, 47);
    ctx.stroke();
  }
  // Stays from the masthead to bow and stern.
  ctx.strokeStyle = "rgba(60, 50, 40, 0.6)";
  ctx.beginPath();
  ctx.moveTo(mx + 1, 9);
  ctx.lineTo(bow - 4, wl - 26);
  ctx.moveTo(mx + 1, 9);
  ctx.lineTo(stern + 4, wl - 25);
  ctx.stroke();
  // The pennant at the masthead, silver.
  ctx.fillStyle = "#cfd6e0";
  ctx.beginPath();
  ctx.moveTo(mx + 3, 4);
  ctx.lineTo(mx + 18, 7);
  ctx.lineTo(mx + 3, 10);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = WOOD.mast;
  ctx.fillRect(mx, 3, 3, 6);

  // Ripples along the waterline.
  ctx.strokeStyle = WATER.line;
  ctx.lineWidth = 1;
  for (const [x, y, w] of [[stern + 2, wl + 5, 16], [SHIP_W / 2 - 10, wl + 8, 20], [bow - 18, wl + 5, 16]] as const) {
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.quadraticCurveTo(x + w / 2, y - 2, x + w, y);
    ctx.stroke();
  }
  standing = cv;
  return cv;
}

/**
 * The ship from above, over the water its tile has already been painted
 * with: a long pale hull pointed at the bow, the mast, and the furled sail
 * across it.
 */
export function shipFlat(ctx: Ctx, tile: number) {
  const s = tile / 32;
  ctx.fillStyle = HULL.dark;
  ctx.beginPath();
  ctx.ellipse(16 * s, 17 * s, 14 * s, 6 * s, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = HULL.light;
  ctx.beginPath();
  ctx.moveTo(3 * s, 16 * s);
  ctx.quadraticCurveTo(14 * s, 9 * s, 30 * s, 16 * s);
  ctx.quadraticCurveTo(14 * s, 23 * s, 3 * s, 16 * s);
  ctx.fill();
  ctx.fillStyle = SAIL.light;
  ctx.fillRect(14 * s, 7 * s, 3 * s, 18 * s);
  ctx.fillStyle = WOOD.mast;
  ctx.fillRect(15 * s, 15 * s, 2 * s, 2 * s);
}
